import type { PropsWithChildren } from "react";
import { useEffect } from "react";

import Animated, {
  Easing,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withDelay,
  withTiming,
} from "react-native-reanimated";

enum ScaleInDuration {
  Default = 900,
}

enum ScaleInDefaults {
  InitialScale = 0.86,
}

const ScaleInEasing = Easing.bezier(0.2, 0.8, 0.3, 1);

interface ScaleInViewProps extends PropsWithChildren {
  delay?: number;
}

/** Fades in while scaling from 0.86 to 1. Duration 900ms with a deceleration curve. */
const ScaleInView = ({ children, delay = 0 }: ScaleInViewProps) => {
  const isReducedMotion = useReducedMotion();
  const opacity = useSharedValue(isReducedMotion ? 1 : 0);
  const scale = useSharedValue(
    isReducedMotion ? 1 : ScaleInDefaults.InitialScale,
  );

  useEffect(() => {
    if (isReducedMotion) return;
    const config = {
      duration: ScaleInDuration.Default,
      easing: ScaleInEasing,
    };
    opacity.value = withDelay(delay, withTiming(1, config));
    scale.value = withDelay(delay, withTiming(1, config));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once on mount
  }, []);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ scale: scale.value }],
  }));

  return <Animated.View style={animatedStyle}>{children}</Animated.View>;
};

export type { ScaleInViewProps };
export { ScaleInView };
